import {html} from 'lit-html';
import TemplateRegistry from '../template/template-registry';
import {ThemeConfig, ThemeCurrency} from '../const';
import {formatMoney} from '../money';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';
import Translate from '../translate';

import './price-summary.option.css';

/**
 * Price Summary - displays pricing breakdown with configurable display options
 * params: totalOnly, showBasePrice, showAddons, showAddonDetails, summaryTemplate
 */
TemplateRegistry.templates['price-summary'] = (option, config, onChange, onErrorClear, getOption) => {
    if (!ThemeConfig.priceVisible || !config.price) return html``;

    const params = option.params || {};
    const basePrice = config.price.final - config.price.total;
    const addOnPrice = config.price.total;
    const totalPrice = config.price.final;
    
    // Helper to format money
    const money = (price) => formatMoney(price * 100, ThemeCurrency.moneyFormat);
    
    const parseAddonValues = (option, optionValue) => {
        if (!option.values) return null;

        const selectedValues = optionValue.split(',').map(v => v.trim()).filter(Boolean);
        const isMultiValue = (option.params?.multiSelect || selectedValues.length > 1) && selectedValues.length;
        
        // Helper to find option value by matching string
        const findValue = (val) => option.values.find(ov => String(ov.value).trim() === String(val).trim());
        
        if (params.showAddonDetails !== false && isMultiValue) {
            return {
                showSubValuePrices: true,
                subValues: selectedValues
                    .map(val => findValue(val))
                    .filter(Boolean)
                    .map(v => ({text: v.text || v.value, price: v.price || 0}))
            };
        }

        if (params.showAddonDetails === false && isMultiValue) return {};

        const matched = findValue(optionValue);
        return {valueText: matched ? (matched.text || matched.value) : optionValue};
    };

    const addons = Object.entries(config.price.options || {})
        .map(([name, data]) => {
            const price = typeof data === 'number' ? data : data?.price || 0;
            const details = data?.option?.value ? parseAddonValues(data.option, data.option.value) : null;
            // lookup option .label for add-on name if available
            const option = getOption(name);
            if (option?.label) name = option.label;
            return {name, price, ...details};
        })
        .filter(addon => addon.price !== 0);

    // determine if we have anything to show in the summary template (if provided)
    const hasSummaryTemplate = !!params.summaryTemplate && (!params.summaryTemplate.includes('{addon}') || addOnPrice > 0)

    // hide the summary box if there is nothing to show:
    // - showTotal is false
    // - showBasePrice is false
    // - and there are no selected add-ons (or add-ons display is disabled)
    const hideEmptyBox = (!params.showTotal)
        && (!params.showBasePrice)
        && (!params.showAddons || addons.length === 0) && !hasSummaryTemplate;

    if (hideEmptyBox) return html``;

    return html`
        <div class="cl-po--price-summary">
            ${params.showBasePrice !== false ? html`
                <div class="cl-po--price-summary-row cl-po--price-summary-base">
                    <span class="cl-po--price-summary-label">${Translate.get('product.options.summary.base', {}, 'Base Price')}:</span>
                    <span class="cl-po--price-summary-value">${unsafeHTML(money(basePrice))}</span>
                </div>
            ` : null}

            ${params.showAddons !== false && addons.length ? html`
                <div class="cl-po--price-summary-addons ${!params.showBasePrice ? 'cl-no-base' : ''} ${!params.showTotal ? 'cl-no-border-btm' : ''}">
                    ${params.showAddonDetails === false ? html`
                        <div class="cl-po--price-summary-row cl-po--price-summary-addons-total">
                            <span class="cl-po--price-summary-label">${Translate.get('product.options.summary.addon', {}, 'Add-ons')}:</span>
                            <span class="cl-po--price-summary-value">${unsafeHTML(money(addOnPrice))}</span>
                        </div>
                    ` : html`
                        <div class="cl-po--price-summary-addons-header">${Translate.get('product.options.summary.addon', {}, 'Add-ons')}:</div>
                        ${addons.map(addon => html`
                            <div class="cl-po--price-summary-addon-group">
                                ${addon.showSubValuePrices ? html`
                                    <div class="cl-po--price-summary-addon-label-only">
                                        <span class="cl-po--price-summary-label">${addon.name}</span>
                                    </div>
                                    <div class="cl-po--price-summary-sub-values">
                                        ${addon.subValues.map(subVal => html`
                                            <div class="cl-po--price-summary-row cl-po--price-summary-sub-value">
                                                <span class="cl-po--price-summary-label">• ${subVal.text}</span>
                                                <span class="cl-po--price-summary-value">${unsafeHTML(money(subVal.price))}</span>
                                            </div>
                                        `)}
                                    </div>
                                ` : html`
                                    <div class="cl-po--price-summary-row cl-po--price-summary-addon">
                                        <span class="cl-po--price-summary-label">${addon.name}${addon.valueText ? ` (${addon.valueText})` : ''}</span>
                                        <span class="cl-po--price-summary-value">${unsafeHTML(money(addon.price))}</span>
                                    </div>
                                `}
                            </div>
                        `)}
                    `}
                </div>
            ` : null}
                                 
            ${params.showTotal ? html`<div class="cl-po--price-summary-row cl-po--price-summary-total">
                <span class="cl-po--price-summary-label">${Translate.get('product.options.summary.total', {}, 'Total')}:</span>
                <span class="cl-po--price-summary-value">${unsafeHTML(money(totalPrice))}</span>
            </div>` : null}

            ${hasSummaryTemplate ? html`
                <div class="cl-po--price-summary-text">
                    ${unsafeHTML(params.summaryTemplate
                        .replace(/{base}/g, `<b>${money(basePrice)}</b>`)
                        .replace(/{addon}/g, `<b>${money(addOnPrice)}</b>`)
                        .replace(/{total}/g, `<b>${money(totalPrice)}</b>`)
                    )}
                </div>
            ` : null}
        </div>
    `;
};
